/**
 * @file Register.jsx
 * @description Handles user authentication and session management
 *
 * @section Responsibilities
 * - Manage authentication flow
 * - Handle form validation
 * - Initialize user session
 * - Handle authentication errors
 *
 * @section Dependencies
 * - Session Management: User session handling
 * - Form Validation: Input validation
 * - API Services: Authentication requests
 *
 * @section Integration Points
 * - Authentication API: User verification
 * - Session System: Session management
 * - Navigation: Post-auth routing
 *
 * @section State Management
 * - formData: User input state
 * - loading: Process status
 * - error: Error handling
 *
 * @section Events
 * - onSubmit: Form submission
 * - onError: Error handling
 * - onSuccess: Successful authentication
 *
 * @section Props
 * @prop {Function} onSuccess - Success callback
 * @prop {Boolean} autoRedirect - Auto-redirect control
 */

import axios from "axios";
import { useState } from "react";
import "./../../assets/fonts.css";
import "./../../styles/global.css";
import "./../../styles/register.css";
import { Grid } from "@mui/material";
import { useForm } from "../../utils/useForm";
import TextInput from "./../../components/forms/TextInput";
import BallsBackground from "../../components/BallsBackground";
import DgButton from "../../components/DgButton";
import { useNavigate } from "react-router-dom";

/**
 * @file Register.jsx
 * @description Handles user registration and account creation
 *
 * @section Responsibilities
 * - Collect user registration information
 * - Validate registration data
 * - Handle account creation
 * - Manage registration state
 * - Process form submission
 * - Handle validation errors
 *
 * @section Dependencies
 * - AuthService: User authentication
 * - ValidationService: Form validation
 * - RegistrationService: Account creation
 * - StateManagement: Form state handling
 *
 * @section Integration Points
 * - Auth API: User registration
 * - Validation System: Data validation
 * - Email Service: Verification emails
 * - Navigation System: Post-registration routing
 *
 * @section State Management
 * - formData: Registration information
 * - validationState: Form validation
 * - submissionStatus: Registration status
 * - errorState: Error handling
 *
 * @section Events
 * - onInputChange: Form field updates
 * - onValidation: Field validation
 * - onSubmit: Registration submission
 * - onError: Error handling
 *
 * @section Props
 * @prop {Object} initialData - Initial form data
 * @prop {Function} onComplete - Completion callback
 * @prop {Object} validationRules - Form validation rules
 * @prop {Boolean} autoValidate - Controls automatic validation
 */

function Register() {
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [data, setData] = useForm({
    fullName: "",
    email: "",
    password: "",
    phone: "",
    role: "customer",
  });

  const handleChangePhone = (e) => {
    const value = e.target.value;
    // Elimina cualquier carácter que no sea un número
    const numericValue = value.replace(/\D/g, "");
    // Aplica el formato XXX-XXX-XXXX
    const formattedPhone = numericValue
      .slice(0, 10)
      .replace(/(\d{3})(\d{3})(\d{0,4})/, (match, p1, p2, p3) =>
        [p1, p2, p3].filter(Boolean).join("-")
      );
    setData({ target: { value: formattedPhone, name: e.target.name } });
  };

  const navigate = useNavigate();
  const { fullName, email, password, phone } = data;

  const register = () => {
    setLoading(true);
    const [username, domain] = email.split("@");

    if (!domain) {
      setLoading(false);
      setError("Email address is invalid");
      return;
    }
    data.username = `${username}${domain.split(".")[0]}`;

    axios
      .post(`${process.env.REACT_APP_BASE_URL}/user`, data)
      .then((res) => {
        sessionStorage.credentials = JSON.stringify(data);
        localStorage.session = JSON.stringify(res.data.user);

        navigate("/onboarding/confirmation-code");
      })
      .catch((error) => {
        setLoading(false);
        setError(error.response.data.message);
      });
  };

  const isActive = (path) => {
    if (window.location.pathname == path) {
      return "active";
    }
    return "";
  };

  return (
    <BallsBackground>
      <div className="reduced-content reduced-container shadow1">
        <div className="inner-content">
          <div className="auth-select">
            <div
              onClick={() => navigate("/login")}
              className={`auth-option ${isActive("/login")}`}
            >
              <span>Log In</span>
            </div>
            <div className={`auth-option ${isActive("/register")}`}>
              <span>Register</span>
            </div>
          </div>
          <div className="content-wrapper">
            <div className="heading">
              <h1 className="title">Register</h1>
            </div>
            <Grid
              container
              rowSpacing={4}
              columnSpacing={{ xs: 0, sm: 2, md: 12 }}
            >
              <Grid item xs={12}>
                <div className="form-field">
                  <TextInput
                    customInput={{
                      label: "Full name",
                      name: "fullName",
                      value: fullName,
                      action: setData,
                      tooltipBool: false,
                    }}
                  ></TextInput>
                </div>
                <div className="form-field">
                  <TextInput
                    customInput={{
                      type: "email",
                      label: "Email",
                      name: "email",
                      value: email,
                      action: setData,
                      tooltipBool: false,
                    }}
                  ></TextInput>
                </div>
                <div className="form-field">
                  <TextInput
                    customInput={{
                      type: "password",
                      label: "Password",
                      name: "password",
                      value: password,
                      action: setData,
                      tooltipBool: false,
                    }}
                  ></TextInput>
                </div>
                <div className="form-field">
                  <TextInput
                    customInput={{
                      label: "Phone number",
                      name: "phone",
                      value: phone,
                      action: handleChangePhone,
                      tooltipBool: false,
                    }}
                  ></TextInput>
                </div>
              </Grid>
            </Grid>
            <div className="button-container">
              {error && <div className="error">{error}</div>}
              <DgButton
                category={"primary"}
                size={"large"}
                text={"Register"}
                action={register}
                loading={loading}
              ></DgButton>
            </div>
          </div>
        </div>
      </div>
    </BallsBackground>
  );
}

export default Register;
